import React from 'react';
import { EntityTypeEnum, ExpeditionStatusEnum, FeaturesEnum, UserInfoOutDTO } from '@api/ringil-api.v2.tsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';
import EllipsisText from '@components/RingilComponents/EllipsisText';
import { Ringil3BadgeRow } from '@components/ringil3/styles.tsx';
import LabelsTag from '@components/framework/tags/LabelsTag.tsx';
import StateTag from '@components/framework/tags/StateTag.tsx';
import TransportationTypeTag from '@components/framework/tags/TransportationTypeTag.tsx';
import ExpeditionTypeTag from '@components/framework/tags/ExpeditionTypeTag.tsx';
import ManipulationTag from '@components/framework/tags/ManipulationTag.tsx';
import TruckTag from '@components/framework/tags/TruckTag.tsx';
import CargoTypeTag from '@components/framework/tags/CargoTypeTag.tsx';
import CarrierNoteTag from '@components/framework/tags/CarrierNoteTag.tsx';
import RequirementsTag from '@components/framework/tags/RequirementsTag.tsx';
import IncotermsTag from '@components/framework/tags/IncotermsTag.tsx';
import InputTextTag from '@components/RingilComponents/tags/InputTextTag.tsx';
import PolandSentTag from '@components/RingilComponents/tags/PolandSentTag.tsx';
import LinkedSNBadge from '@components/ringil3/Badges/LinkedSNBadge.tsx';
import LinkWithIcon from '@components/ringil3/Links/LinkWithIcon.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton';
import SvgCoCopy from '@icons/CoCopy.tsx';
import SvgEdit from '@icons/pencil-inverted.svg?react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { findErrorMessage } from '@utils/validationUtils.ts';
import { NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import { Form } from '@hooks/useForm/useForm.tsx';

interface ComponentProps {
  form: Form<NewExpeditionForm>;
  loggedUser: UserInfoOutDTO;
  /** True when the form was opened with "link to supplier notification" enabled. */
  showSupplierNotificationLink: boolean;
  onEditName: () => void;
  onPrefillFromFavorites: () => void;
}

/** Shipment name, labels and the shipment-level parameters shown as tags above the route. */
const ExpeditionHeader: React.FC<ComponentProps> = ({ form, loggedUser, showSupplierNotificationLink, onEditName, onPrefillFromFavorites }) => {
  const { tr } = useTranslationLgs();

  /** First message among the paths a tag can fail on — the cargo tag owns both the type and its ADR weight. */
  const errorFor = (...paths: string[]) => paths.map(path => findErrorMessage(form.validationErrors, path, tr)).find(message => message != null);

  return (
    <>
      {/* The name is free text and can be one long unbroken word, which used to push the edit button and
          the labels off to the right. It truncates instead (full name on hover), and everything beside
          it keeps its place: min-w-0 so the heading may shrink at all, shrink-0 on its neighbours. */}
      <div className={'flex min-w-0 flex-row gap-4 items-center'}>
        <COHeadline
          variant={'h2'}
          title={<EllipsisText className="min-w-0">{form.values.name || tr('CreateExpedition.novaExpedice', 'New shipment')}</EllipsisText>}
          className="min-w-0 max-w-full"
        />
        <div className={'flex shrink-0 flex-row gap-4 items-center'}>
          <RingilButton
            mode={'table'}
            icon={<SvgEdit />}
            tooltip={tr('CreateExpedition.editShipmentNameTooltip', 'Edit the shipment name')}
            onClick={onEditName}
            dataTestId="expeditionInfo-expeditionName"
          />
          <LabelsTag
            objectId={null}
            objectType={EntityTypeEnum.EXPEDITION}
            resolveLabelsForType={EntityTypeEnum.EXPEDITION}
            onChange={labels => {
              form.setFieldValue(form.names.labels, labels);
            }}
            value={form.values.labels}
            dataTestId="expeditionInfo-label"
          />
        </div>
      </div>
      <div>
        <LinkWithIcon
          icon={<SvgCoCopy className={'text-brand-color fill-current'} />}
          label={tr('CreateExpedition.prefillFromFavorites', 'Prefill from favorites')}
          onClick={onPrefillFromFavorites}
          dataTestId="prefill"
        />
      </div>
      <div className={'flex flex-col gap-2 my-4'}>
        {showSupplierNotificationLink && (
          <Ringil3BadgeRow>
            <LinkedSNBadge supplierNotifications={form.values.supplierNotificationLink} />
          </Ringil3BadgeRow>
        )}
        <Ringil3BadgeRow>
          <StateTag expeditionState={ExpeditionStatusEnum.NEW} dataTestId="expeditionInfo-status" />
          {loggedUser?.features.indexOf(FeaturesEnum.CUSTOM_EXP_APP_ID) != -1 && (
            <InputTextTag
              label={tr(`CustomerApplicationIdTag.title`, `Vlastní ID expedice`)}
              value={form.values.customApplicationId}
              onChange={v =>
                form.useProduce(d => {
                  d.customApplicationId = v;
                })
              }
              keyfilter={RegExp('[a-zA-Z0-9-]+')}
              dataTestId="expeditionInfo-customShippingId"
              dataTestIdInput="expeditionInfo-customShippingIdInput"
            />
          )}

          <TransportationTypeTag
            transportationType={form.values.transportationType}
            onChange={v => {
              form.useProduce(d => {
                d.transportationType = v;
              });
            }}
          />

          <ExpeditionTypeTag
            value={form.values.expeditionType}
            onChange={v => {
              form.useProduce(d => {
                d.expeditionType = v;
              });
            }}
          />

          <ManipulationTag
            value={form.values.loadingTypes}
            onChange={v =>
              form.useProduce(d => {
                d.loadingTypes = v;
              })
            }
            dataTestId="expeditionInfo-manipulation"
          />

          <TruckTag
            truckTypeCode={form.values.truckTypeCode}
            onChange={v =>
              form.useProduce(d => {
                d.truckTypeCode = v;
              })
            }
          />

          <CargoTypeTag
            cargoType={form.values.cargoType}
            required
            onChange={v =>
              form.useProduce(d => {
                d.cargoType = v;
              })
            }
            errorMessage={errorFor('cargoType', 'cargoType.cargoTypeCode', 'cargoType.adrWeight')}
          />

          <IncotermsTag
            value={form.values.incoterms}
            onChange={v =>
              form.useProduce(d => {
                d.incoterms = v;
              })
            }
            dataTestId="expeditionInfo-incoterms"
            dataTestIdList="expeditionInfo-incotermsList"
            dataTestIdInput="expeditionInfo-incotermsInput"
          />

          <InputTextTag
            label={tr(`InputTextTag.ekaerNumber`, `Ekaer number`)}
            value={form.values.huEkaerNumber}
            onChange={(value: string) => {
              form.useProduce(d => {
                d.huEkaerNumber = value;
              });
            }}
            dataTestId="expeditionInfo-ekaerNumber"
            dataTestIdInput="expeditionInfo-ekaerNumberInput"
          />

          <InputTextTag
            label={tr(`InputTextTag.uitNumber`, `Uit number`)}
            value={form.values.roUitNumber}
            onChange={(value: string) => {
              form.useProduce(d => {
                d.roUitNumber = value;
              });
            }}
            dataTestId="expeditionInfo-uitNumber"
            dataTestIdInput="expeditionInfo-uitNumberInput"
          />

          <InputTextTag
            label={tr(`InputTextTag.utnCode`, `UTN code`)}
            value={form.values.utnCode}
            onChange={(value: string) => {
              form.useProduce(d => {
                d.utnCode = value;
              });
            }}
          />

          <PolandSentTag
            value={form.values.polandSent}
            onChange={value => {
              form.useProduce(d => {
                d.polandSent = value;
              });
            }}
            dataTestId="expeditionInfo-polandSentSystem"
            dataTestIdDeclarationRefNumberInput="expeditionInfo-polandSentSystem-declarationRefNumberInput"
            dataTestIdCarrierUpdateKeyInput="expeditionInfo-polandSentSystem-carrierUpdateKeyInput"
          />
        </Ringil3BadgeRow>
        <Ringil3BadgeRow>
          {loggedUser && (
            <RequirementsTag
              valueCodes={form.values.specialRequirements}
              onChange={v => {
                form.useProduce(draft => {
                  draft.specialRequirements = v;
                });
              }}
              prefillEnabled={true}
              companyId={loggedUser.companyId}
              dataTestId="expeditionInfo-carrierRequirements"
            />
          )}

          <CarrierNoteTag
            value={form.values.carrierNote}
            onChange={v =>
              form.useProduce(draft => {
                draft.carrierNote = v;
              })
            }
            dataTestId="expeditionInfo-noteForCarrier"
            dataTestIdInput="expeditionInfo-noteForCarrierInput"
          />
        </Ringil3BadgeRow>
      </div>
    </>
  );
};

export default ExpeditionHeader;
